import React, { useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { useHistory, useLocation } from "react-router-dom";
import {
  Box,
  Container,
  Grid,
  Typography,
  Stack,
  Pagination,
  IconButton,
  Divider,
} from "@mui/material";
import {
  Bell,
  Ticket,
  Trophy,
  Gift,
  Handshake,
  Coins,
  DollarSign,
  Building2,
  Newspaper,
  Flame,
  Search,
  Download,
  RefreshCw,
  Calendar,
  ChevronRight,
  Activity,
  ArrowUpDown,
  X,
  ExternalLink,
} from "lucide-react";
import { format, formatDistanceToNow, subDays, isAfter } from "date-fns";
import * as XLSX from "xlsx";

import { colors, customShadows } from "../../../../theme";
import { Path } from "../../../../static/Constants";
import {
  CommonButton,
  CommonChip,
  CommonInput,
  CommonSelect,
  CommonTable,
  CommonDialog,
} from "../../ui";
import { DashboardLayout } from "../layout";

import web3 from "../../../Service/web3";
import cinstance from "../../../Service/randomcinstance";
import usercinstance from "../../../Service/usercinstance";
import Loaders from "../../Loaders";

// Type configurations for icons, badges, and thematic categories
const NOTIFICATION_CONFIG = {
  LotteryCreated: {
    category: "lottery",
    categoryLabel: "Lottery",
    badgeLabel: "Lottery Created",
    chipColor: colors.primary.main,
    chipBg: colors.blue[50],
    icon: Ticket,
    iconColor: colors.primary.main,
    iconBg: colors.blue[50],
  },
  LotteryCreatedAdmin: {
    category: "lottery",
    categoryLabel: "Lottery",
    badgeLabel: "Admin Lottery",
    chipColor: colors.primary.main,
    chipBg: colors.blue[50],
    icon: Ticket,
    iconColor: colors.primary.main,
    iconBg: colors.blue[50],
  },
  "RolloverHappened'": {
    category: "lottery",
    categoryLabel: "Lottery",
    badgeLabel: "Rollover",
    chipColor: colors.amber[700],
    chipBg: colors.amber[50],
    icon: Flame,
    iconColor: colors.amber[600],
    iconBg: colors.amber[50],
  },
  RolloverHappened: {
    category: "lottery",
    categoryLabel: "Lottery",
    badgeLabel: "Rollover",
    chipColor: colors.amber[700],
    chipBg: colors.amber[50],
    icon: Flame,
    iconColor: colors.amber[600],
    iconBg: colors.amber[50],
  },
  LotteryBought: {
    category: "lottery",
    categoryLabel: "Lottery",
    badgeLabel: "Ticket Purchase",
    chipColor: colors.emerald[700],
    chipBg: colors.emerald[50],
    icon: Coins,
    iconColor: colors.emerald[600],
    iconBg: colors.emerald[50],
  },
  LotteryResult: {
    category: "winner",
    categoryLabel: "Winners & Payouts",
    badgeLabel: "Lottery Draw",
    chipColor: colors.amber[800],
    chipBg: colors.amber[50],
    icon: Trophy,
    iconColor: colors.amber[600],
    iconBg: colors.amber[50],
  },
  SpinLotteryResult: {
    category: "winner",
    categoryLabel: "Winners & Payouts",
    badgeLabel: "Spin Draw",
    chipColor: colors.amber[800],
    chipBg: colors.amber[50],
    icon: Trophy,
    iconColor: colors.amber[600],
    iconBg: colors.amber[50],
  },
  LotteryWinner: {
    category: "winner",
    categoryLabel: "Winners & Payouts",
    badgeLabel: "Winner Declared",
    chipColor: colors.amber[800],
    chipBg: colors.amber[50],
    icon: Trophy,
    iconColor: colors.amber[600],
    iconBg: colors.amber[50],
  },
  WinnerPaid: {
    category: "winner",
    categoryLabel: "Winners & Payouts",
    badgeLabel: "Winner Payout",
    chipColor: colors.emerald[700],
    chipBg: colors.emerald[50],
    icon: DollarSign,
    iconColor: colors.emerald[600],
    iconBg: colors.emerald[50],
  },
  PartnerPaid: {
    category: "partner",
    categoryLabel: "Partners & Orgs",
    badgeLabel: "Partner Payout",
    chipColor: colors.emerald[700],
    chipBg: colors.emerald[50],
    icon: DollarSign,
    iconColor: colors.emerald[600],
    iconBg: colors.emerald[50],
  },
  GiftCardCreated: {
    category: "giftcard",
    categoryLabel: "Gift Cards",
    badgeLabel: "Gift Card Created",
    chipColor: colors.purple[700],
    chipBg: colors.purple[50],
    icon: Gift,
    iconColor: colors.purple[600],
    iconBg: colors.purple[50],
  },
  GiftCardPurchased: {
    category: "giftcard",
    categoryLabel: "Gift Cards",
    badgeLabel: "Gift Card Purchased",
    chipColor: colors.purple[700],
    chipBg: colors.purple[50],
    icon: Gift,
    iconColor: colors.purple[600],
    iconBg: colors.purple[50],
  },
  OrganisationAdded: {
    category: "partner",
    categoryLabel: "Partners & Orgs",
    badgeLabel: "Creator Added",
    chipColor: colors.cyan[700],
    chipBg: colors.cyan[50],
    icon: Building2,
    iconColor: colors.cyan[600],
    iconBg: colors.cyan[50],
  },
  PartnerCreated: {
    category: "partner",
    categoryLabel: "Partners & Orgs",
    badgeLabel: "Partner Created",
    chipColor: colors.blue[700],
    chipBg: colors.blue[50],
    icon: Handshake,
    iconColor: colors.blue[600],
    iconBg: colors.blue[50],
  },
  PlatformUpdate: {
    category: "platform",
    categoryLabel: "Platform",
    badgeLabel: "Platform Update",
    chipColor: colors.slate[700],
    chipBg: colors.slate[100],
    icon: Newspaper,
    iconColor: colors.slate[600],
    iconBg: colors.slate[100],
  },
};

const getTypeConfig = (type) => {
  return (
    NOTIFICATION_CONFIG[type] || {
      category: "other",
      categoryLabel: "General",
      badgeLabel: type || "System Event",
      chipColor: colors.slate[700],
      chipBg: colors.slate[100],
      icon: Bell,
      iconColor: colors.primary.main,
      iconBg: colors.blue[50],
    }
  );
};

const PAGE_SIZE = 10;

const withTimeout = (promise, ms = 4000) => {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error("Request timed out")), ms),
    ),
  ]);
};

/**
 * NotificationListPage Component
 * Unified notification hub supporting both Admin and Creator views dynamically.
 */
const NotificationListPage = ({ role }) => {
  const history = useHistory();
  const location = useLocation();
  const apiUrl = process.env.REACT_APP_API_KEY;

  // Determine active role: 'admin' | 'creator'
  const resolvedRole =
    role || (location.pathname.startsWith("/admin") ? "admin" : "creator");
  const isAdmin = resolvedRole === "admin";

  // Auth & Permissions states
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [accountAddress, setAccountAddress] = useState("");
  const [creatorInfo, setCreatorInfo] = useState({
    name: "Creator",
    active: true,
  });

  // Data states
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);

  // Detail Dialog State
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);

  // Filtering & Search
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [dateFilter, setDateFilter] = useState("all"); // 'all' | 'today' | 'week' | 'month' | 'custom'
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [sortBy, setSortBy] = useState("newest"); // 'newest' | 'oldest'

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Format relative time safely
  const formatTimeAgo = (dateTimeString) => {
    if (!dateTimeString) return "Recently";
    try {
      const date = new Date(dateTimeString);
      return formatDistanceToNow(date, { addSuffix: true });
    } catch {
      return "Recently";
    }
  };

  // Format absolute date
  const formatExactDate = (dateTimeString) => {
    if (!dateTimeString) return "-";
    try {
      const date = new Date(dateTimeString);
      return format(date, "MMM d, yyyy • h:mm a");
    } catch {
      return dateTimeString;
    }
  };

  // Verify Role Permissions
  const verifyAuth = useCallback(async () => {
    if (window.ethereum || web3) {
      window.web3 = web3;
      try {
        let accounts = [];
        if (window.ethereum?.request) {
          accounts = await window.ethereum.request({ method: "eth_accounts" });
        }
        if (!accounts || accounts.length === 0) {
          accounts = await web3.eth.getAccounts();
        }
        const userAccount =
          accounts && accounts[0]
            ? accounts[0]
            : window.ethereum?.selectedAddress || "";

        if (userAccount) {
          setAccountAddress(userAccount);
        }

        if (isAdmin) {
          let adminContractAddr = "";
          try {
            if (cinstance?.methods?.admin) {
              adminContractAddr = await withTimeout(
                cinstance.methods.admin().call(),
                3500,
              );
            }
          } catch (adminErr) {
            console.warn("Could not fetch contract admin:", adminErr);
          }

          const authorized = Boolean(
            adminContractAddr &&
              userAccount &&
              adminContractAddr.toLowerCase() === userAccount.toLowerCase(),
          );
          setIsAuthorized(authorized);
        } else {
          // Creator permission check
          try {
            if (userAccount && usercinstance?.methods?.organisationbyaddr) {
              const orgData = await withTimeout(
                usercinstance.methods.organisationbyaddr(userAccount).call(),
                3500,
              );
              setCreatorInfo(orgData || { name: "Creator", active: true });
              setIsAuthorized(Boolean(orgData ? orgData.active : true));
            } else {
              setCreatorInfo({ name: "Creator", active: true });
              setIsAuthorized(true);
            }
          } catch (orgErr) {
            console.warn("Could not fetch creator info:", orgErr);
            setCreatorInfo({ name: "Creator", active: true });
            setIsAuthorized(true);
          }
        }
        setAuthChecked(true);
      } catch (err) {
        console.error("Auth verification error:", err);
        setIsAuthorized(false);
        setAuthChecked(true);
      }
    } else {
      setAuthChecked(true);
    }
  }, [isAdmin]);

  // Fetch all notifications from API
  const fetchNotifications = useCallback(async () => {
    if (!apiUrl) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      let account = "";
      if (window.ethereum) {
        try {
          const accounts = await web3.eth.getAccounts();
          if (accounts && accounts[0]) {
            account = accounts[0];
            setAccountAddress(account);
          }
        } catch (accErr) {
          console.warn("Could not retrieve account for notifications:", accErr);
        }
      }

      let skip = 0;
      let all = [];
      let keepFetching = true;

      while (keepFetching) {
        const response = await fetch(
          `${apiUrl}/notifications?limit=50&skip=${skip}`,
          {
            method: "GET",
            headers: {
              ...(account ? { Authorization: account } : {}),
            },
          },
        );

        if (response.ok) {
          const data = await response.json();
          const batch = data.result || [];
          if (batch.length > 0) {
            all = [...all, ...batch];
            skip += 50;
            if (batch.length < 50) {
              keepFetching = false;
            }
          } else {
            keepFetching = false;
          }
        } else {
          keepFetching = false;
          console.error("Failed to fetch notifications:", response.statusText);
        }
      }

      setNotifications(all);
    } catch (err) {
      console.error("Error fetching notifications:", err);
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    verifyAuth();
    fetchNotifications();
  }, [verifyAuth, fetchNotifications]);

  // Route Resolver based on notification data and user role
  const getNotificationRoute = useCallback(
    (notification) => {
      if (!notification) return null;
      const { type, genId, lotteryName, owner, user } = notification;

      switch (type) {
        case "LotteryCreated":
        case "LotteryCreatedAdmin":
        case "RolloverHappened'":
        case "RolloverHappened":
          if (genId) {
            if (lotteryName === "Missile" || lotteryName === "RocketBET") return Path.getMissileLottery(genId);
            if (lotteryName === "Mine" || lotteryName === "MineBET") return Path.getMineLottery(genId);
            if (lotteryName === "MRL" || lotteryName === "MegaBET") return Path.getRevolverLottery(genId);
            return Path.Spinnerlottery;
          }
          return isAdmin ? Path.adminlotterylist : Path.orgmylottery;

        case "LotteryResult":
        case "SpinLotteryResult":
        case "LotteryWinner":
          if (genId) {
            if (isAdmin || notification.admin === true) {
              return Path.getAdminLotteryDetails(genId);
            } else if (
              accountAddress &&
              accountAddress.toLowerCase() === owner?.toLowerCase()
            ) {
              return Path.getCreatorLotteryDetails(genId);
            } else {
              return Path.result;
            }
          }
          return isAdmin ? Path.adminlotterylist : Path.orgmylottery;

        case "GiftCardCreated":
          return genId
            ? Path.getGiftCardDetails(genId)
            : isAdmin
            ? Path.admingiftcard
            : Path.mygiftcard;

        case "GiftCardPurchased":
          if (genId) {
            return isAdmin
              ? Path.getAdminGiftCardDetails(genId)
              : Path.getCreatorGiftCardDetails(genId);
          }
          return isAdmin ? Path.admingiftcard : Path.mygiftcard;

        case "OrganisationAdded":
          return isAdmin ? Path.adminorglist : Path.org;

        case "LotteryBought":
          if (genId) {
            if (isAdmin || notification.admin === true) {
              return Path.getAdminLotteryDetails(genId);
            } else if (
              accountAddress &&
              accountAddress.toLowerCase() === owner?.toLowerCase()
            ) {
              return Path.getCreatorLotteryDetails(genId);
            } else if (
              accountAddress &&
              accountAddress.toLowerCase() === user?.toLowerCase()
            ) {
              return Path.profile;
            }
            return Path.getCreatorLotteryDetails(genId);
          }
          return isAdmin ? Path.adminlotterylist : Path.orgmylottery;

        case "PartnerCreated":
        case "PartnerPaid":
          return isAdmin ? Path.adminpartnerslist : `/#partners`;

        case "WinnerPaid":
          if (genId) {
            return isAdmin
              ? Path.getAdminLotteryDetails(genId)
              : Path.getCreatorLotteryDetails(genId);
          }
          return isAdmin ? Path.adminlotterylist : Path.orgmylottery;

        case "PlatformUpdate":
        case "News":
          return isAdmin ? Path.adminNews : Path.news;

        default:
          if (genId) {
            return isAdmin
              ? Path.getAdminLotteryDetails(genId)
              : Path.getCreatorLotteryDetails(genId);
          }
          return null;
      }
    },
    [isAdmin, accountAddress],
  );

  // Smart Navigation Handler
  const handleNotificationClick = useCallback(
    (notification) => {
      if (!notification) return;
      const route = getNotificationRoute(notification);
      if (route) {
        history.push(route);
      } else {
        setSelectedNotification(notification);
        setDetailsModalOpen(true);
      }
    },
    [getNotificationRoute, history],
  );

  // Filtered & Sorted notifications computation
  const filteredNotifications = useMemo(() => {
    let list = [...notifications];

    // Category Filter
    if (selectedCategory !== "all") {
      list = list.filter((n) => {
        const config = getTypeConfig(n.type);
        return config.category === selectedCategory;
      });
    }

    // Search Query Filter (searches title, content, genId, type)
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter(
        (n) =>
          n.title?.toLowerCase().includes(q) ||
          n.content?.toLowerCase().includes(q) ||
          n.genId?.toString().toLowerCase().includes(q) ||
          n.type?.toLowerCase().includes(q) ||
          n.lotteryName?.toLowerCase().includes(q),
      );
    }

    // Date Range Filter
    const now = new Date();
    if (dateFilter === "today") {
      list = list.filter((n) =>
        isAfter(new Date(n.createdOn), subDays(now, 1)),
      );
    } else if (dateFilter === "week") {
      list = list.filter((n) =>
        isAfter(new Date(n.createdOn), subDays(now, 7)),
      );
    } else if (dateFilter === "month") {
      list = list.filter((n) =>
        isAfter(new Date(n.createdOn), subDays(now, 30)),
      );
    } else if (dateFilter === "custom" && customStartDate && customEndDate) {
      const start = new Date(customStartDate);
      const end = new Date(customEndDate);
      end.setHours(23, 59, 59, 999);
      list = list.filter((n) => {
        const d = new Date(n.createdOn);
        return d >= start && d <= end;
      });
    }

    // Sorting
    list.sort((a, b) => {
      const dateA = new Date(a.createdOn || 0).getTime();
      const dateB = new Date(b.createdOn || 0).getTime();
      return sortBy === "oldest" ? dateA - dateB : dateB - dateA;
    });

    return list;
  }, [
    notifications,
    selectedCategory,
    searchQuery,
    dateFilter,
    customStartDate,
    customEndDate,
    sortBy,
  ]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [
    searchQuery,
    selectedCategory,
    dateFilter,
    customStartDate,
    customEndDate,
    sortBy,
  ]);

  // Pagination calculation
  const totalPages = Math.max(
    1,
    Math.ceil(filteredNotifications.length / PAGE_SIZE),
  );
  const paginatedNotifications = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredNotifications.slice(start, start + PAGE_SIZE);
  }, [filteredNotifications, currentPage]);

  // Summary Metrics
  const metrics = useMemo(() => {
    const total = notifications.length;
    const now = new Date();
    const recent24h = notifications.filter((n) =>
      isAfter(new Date(n.createdOn || 0), subDays(now, 1)),
    ).length;

    const lotteryCount = notifications.filter((n) => {
      const cat = getTypeConfig(n.type).category;
      return cat === "lottery";
    }).length;

    const winnerCount = notifications.filter((n) => {
      const cat = getTypeConfig(n.type).category;
      return cat === "winner";
    }).length;

    const giftCardCount = notifications.filter((n) => {
      const cat = getTypeConfig(n.type).category;
      return cat === "giftcard";
    }).length;

    const partnerCount = notifications.filter((n) => {
      const cat = getTypeConfig(n.type).category;
      return cat === "partner";
    }).length;

    return {
      total,
      recent24h,
      lotteryCount,
      winnerCount,
      giftCardCount,
      partnerCount,
    };
  }, [notifications]);

  // Export to Excel
  const handleExportExcel = () => {
    const dataToExport = (
      filteredNotifications.length > 0 ? filteredNotifications : notifications
    ).map((n, idx) => ({
      "#": idx + 1,
      Title: n.title || "-",
      Category: getTypeConfig(n.type).categoryLabel,
      Type: n.type || "-",
      Message: n.content || "-",
      "Target ID": n.genId || "-",
      "Lottery Name": n.lotteryName || "-",
      CreatedDate: n.createdOn ? new Date(n.createdOn).toLocaleString() : "-",
    }));

    if (dataToExport.length === 0) return;

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Notifications");
    XLSX.writeFile(
      workbook,
      `AutoBET_Notifications_${isAdmin ? "Admin" : "Creator"}_${format(
        new Date(),
        "yyyyMMdd_HHmm",
      )}.xlsx`,
    );
  };

  // Reset all filters
  const handleResetFilters = () => {
    setSearchQuery("");
    setSelectedCategory("all");
    setDateFilter("all");
    setCustomStartDate("");
    setCustomEndDate("");
    setSortBy("newest");
    setCurrentPage(1);
  };

  const hasActiveFilters =
    searchQuery !== "" ||
    selectedCategory !== "all" ||
    dateFilter !== "all" ||
    sortBy !== "newest";

  // Table Columns Definition
  const tableColumns = useMemo(
    () => [
      {
        id: "type",
        label: "Event Type",
        minWidth: 180,
        render: (_, row) => {
          const config = getTypeConfig(row?.type);
          const IconComponent = config.icon || Bell;
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "8px",
                  bgcolor: config.iconBg,
                  color: config.iconColor,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <IconComponent size={16} />
              </Box>
              <CommonChip
                label={config.badgeLabel}
                color={config.chipColor}
                backgroundColor={config.chipBg}
                size="small"
                sx={{
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  py: 0.4,
                  height: "auto",
                }}
              />
            </Box>
          );
        },
      },
      {
        id: "title",
        label: "Title & Message",
        minWidth: 320,
        render: (_, row) => (
          <Box>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                color: colors.slate[900],
                fontSize: "0.85rem",
                lineHeight: 1.3,
              }}
            >
              {row?.title || "Notification"}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: colors.slate[600],
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                mt: 0.25,
                lineHeight: 1.4,
              }}
            >
              {row?.content || "-"}
            </Typography>
          </Box>
        ),
      },
      {
        id: "genId",
        label: "Target / Entity",
        minWidth: 140,
        render: (_, row) => (
          <Box>
            {row?.genId ? (
              <Typography
                variant="caption"
                sx={{
                  fontFamily: "monospace",
                  bgcolor: colors.slate[100],
                  px: 1,
                  py: 0.4,
                  borderRadius: "6px",
                  color: colors.slate[800],
                  fontWeight: 600,
                  display: "inline-block",
                }}
              >
                ID: {row.genId}
              </Typography>
            ) : (
              <Typography variant="caption" sx={{ color: colors.slate[400] }}>
                -
              </Typography>
            )}
            {row?.lotteryName && (
              <Typography
                variant="caption"
                sx={{ display: "block", color: colors.slate[500], mt: 0.25 }}
              >
                {row.lotteryName}
              </Typography>
            )}
          </Box>
        ),
      },
      {
        id: "createdOn",
        label: "Timestamp",
        minWidth: 180,
        render: (_, row) => (
          <Box>
            <Typography
              variant="body2"
              sx={{
                color: colors.slate[800],
                fontWeight: 600,
                fontSize: "0.8rem",
              }}
            >
              {formatTimeAgo(row?.createdOn)}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: colors.slate[400], display: "block" }}
            >
              {formatExactDate(row?.createdOn)}
            </Typography>
          </Box>
        ),
      },
      {
        id: "actions",
        label: "Action",
        align: "right",
        minWidth: 110,
        render: (_, row) => {
          const route = getNotificationRoute(row);
          return (
            <CommonButton
              variant="outline"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleNotificationClick(row);
              }}
              endIcon={
                route ? <ChevronRight size={14} /> : <ExternalLink size={14} />
              }
            >
              {route ? "View" : "Details"}
            </CommonButton>
          );
        },
      },
    ],
    [getNotificationRoute, handleNotificationClick],
  );

  return (
    <DashboardLayout
      role={resolvedRole}
      title={isAdmin ? "System Notifications" : "Creator Notifications"}
      subtitle={
        isAdmin
          ? "Track lottery draws, winners, ticket sales, gift card activities, and platform alerts."
          : "Track your lottery creations, ticket sales, winner payouts, and gift card updates."
      }
      breadcrumbs={[
        {
          label: "Dashboard",
          href: isAdmin ? Path.admin : Path.org,
        },
        { label: "Notifications" },
      ]}
      headerActions={
        <Stack direction="row" spacing={1.5} alignItems="center">
          <CommonButton
            variant="outline"
            startIcon={<RefreshCw size={16} />}
            onClick={fetchNotifications}
            disabled={loading}
          >
            Refresh
          </CommonButton>
          <CommonButton
            variant="primary"
            startIcon={<Download size={16} />}
            onClick={handleExportExcel}
            disabled={notifications.length === 0}
          >
            Export Excel
          </CommonButton>
        </Stack>
      }
      creatorAddress={accountAddress}
      userName={isAdmin ? "AutoBET Admin" : creatorInfo?.name || "Creator"}
      userRole={isAdmin ? "Administrator" : "Creator"}
      authChecked={authChecked}
      isAuthorized={isAuthorized}
      accessDeniedProps={{
        message: isAdmin
          ? "You must be an administrator to access the System Notifications console."
          : "You must be a registered Creator or organization to view Creator Notifications.",
      }}
    >
      {/* {loading && <Loaders />} */}

      {/* Metrics Ribbon */}
      <Box>
        <Grid container spacing={2.5}  alignItems="stretch">
          {[
            {
              label: "Total Notifications",
              value: metrics.total,
              subtext: isAdmin ? "Logged system events" : "Your notifications",
              icon: <Bell size={22} />,
              iconColor: colors.primary.main,
              iconBg: colors.blue[50],
            },
            {
              label: "Recent Activity (24h)",
              value: metrics.recent24h,
              subtext: "Triggered in last 24 hours",
              icon: <Activity size={22} />,
              iconColor: colors.emerald[600],
              iconBg: colors.emerald[50],
            },
            {
              label: "Lottery & Tickets",
              value: metrics.lotteryCount,
              subtext: "Creations & Purchases",
              icon: <Ticket size={22} />,
              iconColor: colors.purple[600],
              iconBg: colors.purple[50],
            },
            {
              label: "Winners & Payouts",
              value: metrics.winnerCount,
              subtext: "Draw results & claims",
              icon: <Trophy size={22} />,
              iconColor: colors.amber[600],
              iconBg: colors.amber[50],
            },
          ].map((stat, idx) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={3}
              key={`metric-${idx}`}
              sx={{ display: "flex" }}
            >
              <Box
                sx={{
                  width: "100%",
                  p: 2.5,
                  borderRadius: "16px",
                  bgcolor: colors.background.paper,
                  border: `1px solid ${colors.dashboard.cardBorder}`,
                  boxShadow: customShadows.cardFlat,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  transition: "transform 0.2s ease, box-shadow 0.2s ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: customShadows.cardHover,
                  },
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1, pr: 1.5 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.slate[500],
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    {stat.label}
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 800,
                      color: colors.slate[900],
                      mt: 0.5,
                      fontSize: "1.65rem",
                      lineHeight: 1.2,
                    }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.slate[400],
                      mt: 0.25,
                      display: "block",
                    }}
                  >
                    {stat.subtext}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "12px",
                    bgcolor: stat.iconBg,
                    color: stat.iconColor,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {stat.icon}
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
      {/* Filter & Controls Toolbar */}
      <Box
        sx={{
          p: 2.25,
          mb: 3,
          borderRadius: "16px",
          bgcolor: colors.background.paper,
          border: `1px solid ${colors.dashboard.cardBorder}`,
          boxShadow: customShadows.cardFlat,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {/* Row 1: Category Filter Chips & Clear Action */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "stretch", md: "center" },
            justifyContent: "space-between",
            gap: 1.5,
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            sx={{ flexWrap: "wrap", alignItems: "center" }}
          >
            {[
              { key: "all", label: `All (${metrics.total})` },
              {
                key: "lottery",
                label: `Lotteries (${metrics.lotteryCount})`,
              },
              {
                key: "winner",
                label: `Winners (${metrics.winnerCount})`,
              },
              {
                key: "giftcard",
                label: `Gift Cards (${metrics.giftCardCount})`,
              },
              {
                key: "partner",
                label: `Partners & Orgs (${metrics.partnerCount})`,
              },
            ].map((cat) => (
              <CommonChip
                key={cat.key}
                variant="filter"
                label={cat.label}
                selected={selectedCategory === cat.key}
                onClick={() => setSelectedCategory(cat.key)}
              />
            ))}
          </Stack>

          {/* Reset Filters Button */}
          {hasActiveFilters && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                alignSelf: { xs: "flex-end", md: "center" },
              }}
            >
              <CommonButton
                variant="outline"
                size="small"
                startIcon={<X size={14} />}
                onClick={handleResetFilters}
              >
                Clear Filters
              </CommonButton>
            </Box>
          )}
        </Box>

        {/* Row 2: Search Input, Date Range & Sort Dropdowns */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
          }}
        >
          {/* Search Bar */}
          <CommonInput
            placeholder="Search notifications by title, message, ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            width={{ xs: "100%", sm: "320px" }}
            startAdornment={<Search size={18} color={colors.slate[400]} />}
            endAdornment={
              searchQuery ? (
                <IconButton size="small" onClick={() => setSearchQuery("")}>
                  <X size={14} color={colors.slate[400]} />
                </IconButton>
              ) : null
            }
          />

          {/* Date & Sort Filters */}
          <Stack
            direction="row"
            spacing={1.5}
            sx={{
              flexWrap: "wrap",
              alignItems: "center",
              width: { xs: "100%", sm: "auto" },
            }}
          >
            <CommonSelect
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              width="170px"
              startAdornment={<Calendar size={16} color={colors.slate[500]} />}
              options={[
                { value: "all", label: "All Dates" },
                { value: "today", label: "Past 24 Hours" },
                { value: "week", label: "Past 7 Days" },
                { value: "month", label: "Past 30 Days" },
                { value: "custom", label: "Custom Range" },
              ]}
            />

            {dateFilter === "custom" && (
              <Stack direction="row" spacing={1} alignItems="center">
                <CommonInput
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  width="145px"
                />
                <Typography variant="caption" sx={{ color: colors.slate[400] }}>
                  to
                </Typography>
                <CommonInput
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  width="145px"
                />
              </Stack>
            )}

            <CommonSelect
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              width="155px"
              startAdornment={
                <ArrowUpDown size={16} color={colors.slate[500]} />
              }
              options={[
                { value: "newest", label: "Newest First" },
                { value: "oldest", label: "Oldest First" },
              ]}
            />
          </Stack>
        </Box>
      </Box>

      {/* Notification Table View */}
      {filteredNotifications.length === 0 ? (
        <Box
          sx={{
            py: 8,
            px: 3,
            textAlign: "center",
            bgcolor: colors.background.paper,
            borderRadius: "16px",
            border: `1px dashed ${colors.slate[300]}`,
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              bgcolor: colors.slate[100],
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2,
              color: colors.slate[400],
            }}
          >
            <Bell size={32} />
          </Box>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: colors.slate[800] }}
          >
            No Notifications Found
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: colors.slate[500],
              maxWidth: 420,
              mx: "auto",
              mt: 0.5,
              mb: 2.5,
            }}
          >
            {hasActiveFilters
              ? "Try modifying your search keywords or adjusting your category/date filters."
              : "No notifications have been recorded yet."}
          </Typography>
          {hasActiveFilters && (
            <CommonButton
              variant="outline"
              size="small"
              onClick={handleResetFilters}
            >
              Clear All Filters
            </CommonButton>
          )}
        </Box>
      ) : (
        <Box
          sx={{
            bgcolor: colors.background.paper,
            borderRadius: "16px",
            border: `1px solid ${colors.dashboard.cardBorder}`,
            boxShadow: customShadows.cardFlat,
            overflow: "hidden",
          }}
        >
          <CommonTable
            columns={tableColumns}
            rows={paginatedNotifications}
            rowKey={(row, idx) => row._id || `notify-${idx}`}
            onRowClick={(row) => handleNotificationClick(row)}
            hover
          />
        </Box>
      )}

      {/* Pagination Controls */}
      {filteredNotifications.length > 0 && (
        <Box
          sx={{
            mt: 3.5,
            mb: 2,
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: colors.slate[500], fontSize: "0.85rem" }}
          >
            Showing{" "}
            <strong>
              {(currentPage - 1) * PAGE_SIZE + 1}–
              {Math.min(currentPage * PAGE_SIZE, filteredNotifications.length)}
            </strong>{" "}
            of <strong>{filteredNotifications.length}</strong> notifications
          </Typography>

          {totalPages > 1 && (
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(_, page) => setCurrentPage(page)}
              color="primary"
              shape="rounded"
              sx={{
                "& .MuiPaginationItem-root": {
                  fontWeight: 600,
                  borderRadius: "8px",
                },
                "& .Mui-selected": {
                  bgcolor: `${colors.primary.main} !important`,
                  color: "#ffffff",
                },
              }}
            />
          )}
        </Box>
      )}

      {/* Notification Details Dialog */}
      {selectedNotification && (
        <CommonDialog
          open={detailsModalOpen}
          onClose={() => setDetailsModalOpen(false)}
          title="Notification Details"
          subtitle="Complete event metadata and parameters"
          maxWidth="sm"
          actions={
            <Stack
              direction="row"
              spacing={1.5}
              justifyContent="flex-end"
              sx={{ width: "100%" }}
            >
              <CommonButton
                variant="outline"
                onClick={() => setDetailsModalOpen(false)}
              >
                Close
              </CommonButton>
              {getNotificationRoute(selectedNotification) && (
                <CommonButton
                  variant="primary"
                  endIcon={<ExternalLink size={16} />}
                  onClick={() => {
                    const r = getNotificationRoute(selectedNotification);
                    setDetailsModalOpen(false);
                    if (r) history.push(r);
                  }}
                >
                  Navigate to Target
                </CommonButton>
              )}
            </Stack>
          }
        >
          <Box sx={{ py: 1 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                mb: 2,
              }}
            >
              <CommonChip
                label={getTypeConfig(selectedNotification.type).badgeLabel}
                color={getTypeConfig(selectedNotification.type).chipColor}
                backgroundColor={
                  getTypeConfig(selectedNotification.type).chipBg
                }
                size="small"
              />
              {selectedNotification.genId && (
                <Typography
                  variant="caption"
                  sx={{
                    fontFamily: "monospace",
                    bgcolor: colors.slate[100],
                    px: 1,
                    py: 0.4,
                    borderRadius: "6px",
                    fontWeight: 600,
                  }}
                >
                  ID: {selectedNotification.genId}
                </Typography>
              )}
            </Box>

            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: colors.slate[900],
                mb: 1,
              }}
            >
              {selectedNotification.title || "Notification"}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: colors.slate[700],
                lineHeight: 1.6,
                mb: 3,
              }}
            >
              {selectedNotification.content || "No description provided."}
            </Typography>

            <Divider
              sx={{
                mb: 2,
                borderColor: colors.dashboard.cardBorder,
              }}
            />

            {/* Metadata table */}
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography
                    variant="caption"
                    sx={{ color: colors.slate[500], fontWeight: 600 }}
                  >
                    Event Type
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: colors.slate[800],
                      mt: 0.25,
                    }}
                  >
                    {selectedNotification.type || "-"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    variant="caption"
                    sx={{ color: colors.slate[500], fontWeight: 600 }}
                  >
                    Created On
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: colors.slate[800],
                      mt: 0.25,
                    }}
                  >
                    {formatExactDate(selectedNotification.createdOn)}
                  </Typography>
                </Grid>
                {selectedNotification.lotteryName && (
                  <Grid item xs={6}>
                    <Typography
                      variant="caption"
                      sx={{ color: colors.slate[500], fontWeight: 600 }}
                    >
                      Lottery Name
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: colors.slate[800],
                        mt: 0.25,
                      }}
                    >
                      {selectedNotification.lotteryName}
                    </Typography>
                  </Grid>
                )}
                {selectedNotification.owner && (
                  <Grid item xs={12}>
                    <Typography
                      variant="caption"
                      sx={{ color: colors.slate[500], fontWeight: 600 }}
                    >
                      Owner / Creator Address
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "monospace",
                        fontSize: "0.8rem",
                        wordBreak: "break-all",
                        color: colors.slate[800],
                        mt: 0.25,
                      }}
                    >
                      {selectedNotification.owner}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Box>
        </CommonDialog>
      )}
    </DashboardLayout>
  );
};

NotificationListPage.propTypes = {
  role: PropTypes.oneOf(["admin", "creator"]),
};

NotificationListPage.defaultProps = {
  role: undefined,
};

export default NotificationListPage;
